import React from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../core/branding/BrandingContext";
import { LoadingScreen } from "../components/feedback/LoadingScreen";

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding, isLoading, error, projectName } = useBranding();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error Loading Platform</h1>
          <p className="text-gray-400">{error || "Failed to load branding"}</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    navigate("/login");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: branding.backgroundColor }}
    >
      {/* Header */}
      <header className="py-4 md:py-6 xl:py-8 px-8 md:px-12 xl:px-16 2xl:px-20 flex justify-between items-center">
        {branding.logoUrl && (
          <img
            src={branding.logoUrl}
            alt={`${projectName} logo`}
            className="h-16 md:h-20 xl:h-24 2xl:h-28"
            style={{ width: "auto", objectFit: "contain", display: "block" }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        <button
          onClick={handleLogin}
          className="px-4 py-2 md:px-6 md:py-2 xl:px-8 xl:py-3 2xl:px-10 2xl:py-3 text-sm md:text-base xl:text-lg 2xl:text-xl rounded-lg font-semibold transition-colors"
          style={{
            backgroundColor: branding.primaryColor,
            color: "#ffffff",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = branding.secondaryColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = branding.primaryColor;
          }}
        >
          Login
        </button>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex items-center justify-center px-8 md:px-12 xl:px-16 2xl:px-20 py-12 md:py-16 xl:py-20">
        <div className="max-w-2xl md:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl text-center">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={`${projectName} logo`}
              className="h-20 md:h-24 xl:h-28 2xl:h-32 mx-auto mb-6 md:mb-8 xl:mb-10 2xl:mb-12 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}

          <h1
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold mb-4 md:mb-6 xl:mb-8"
            style={{ color: branding.primaryColor }}
          >
            {branding.headingText}
          </h1>

          <h2
            className="text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-4xl mb-4 md:mb-6 xl:mb-8"
            style={{ color: branding.secondaryColor }}
          >
            {branding.subheadingText}
          </h2>

          <p
            className="text-base md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl mb-8 md:mb-10 xl:mb-12 2xl:mb-16 max-w-2xl md:max-w-3xl xl:max-w-4xl mx-auto leading-relaxed"
            style={{ color: "#666" }}
          >
            {branding.landingDescription}
          </p>

          <button
            onClick={handleLogin}
            className="px-8 py-3 md:px-10 md:py-4 xl:px-12 xl:py-4 2xl:px-16 2xl:py-5 rounded-lg text-base md:text-lg xl:text-xl 2xl:text-2xl font-semibold transition-all transform hover:scale-105"
            style={{
              backgroundColor: branding.primaryColor,
              color: "#ffffff",
            }}
          >
            Get Started
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer
        className="py-4 md:py-6 xl:py-8 text-center text-xs md:text-sm xl:text-base 2xl:text-lg"
        style={{ color: "#999" }}
      >
        <p>
          © {new Date().getFullYear()} {branding.headingText}.{" "}
          <span className="text-blue-600">
            {branding.footerText || "All rights reserved"}
          </span>
        </p>
      </footer>
    </div>
  );
};
